<template>
  <v-card>

    <v-data-table :headers="selectedHeaders"
                  class="flex-grow-1"
                  :items="presentations"
                  :items-per-page="form.per_page"
                  :footer-props="{'items-per-page-options': [10, 25, 50, 100]}"
                  :loading="form.busy"
                  loading-text="Loading content..."
                  :server-items-length="meta ? meta.pagination.total : 0"
                  @update:page="updatePage"
                  @update:items-per-page="updateItemsPerPage"
                  @update:sort-by="updateSortBy"
                  @update:options="updateOptions">

      <template v-slot:top="data">

        <div class="d-flex justify-space-between px-2 py-2">

          <filter-presentations-menu :form="form"
                                     @search="search">
          </filter-presentations-menu>

          <v-btn icon
                 rounded>
            <v-icon>
              fal fa-cog
            </v-icon>
          </v-btn>

        </div>

      </template>

      <template v-slot:item.name="{item: presentation}">
        <router-link :to="{name: 'presentation.info', params: {presentationID: presentation.id}}">
          {{ presentation.name }}
        </router-link>
      </template>

      <template v-slot:item.external_id="{item: presentation}">
        <presentation-vault-icon v-if="team.uses_veeva_vault && presentation.vaultConfiguration"
                                 :presentation="presentation">
        </presentation-vault-icon>
        <template v-else>
          {{ presentation.external_id }}
        </template>
      </template>

      <template v-slot:item.size="{item: presentation}">
        {{ presentation.size | filesize(0, false) }}
      </template>

      <template v-slot:item.groups="{item: presentation}">
        <v-menu tile
                v-if="presentation.groups.length">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon
                   v-bind="attrs"
                   v-on="on">
              <v-icon>
                fal fa-ellipsis-v
              </v-icon>
            </v-btn>
          </template>
          <v-list flat
                  tile
                  :elevation="0">
            <v-list-item v-for="group in presentation.groups"
                         :key="group.id"
                         :to="{name: 'group.edit', params: {groupID: group.id}}">
              <v-list-item-content>
                <v-list-item-title>
                  {{ group.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:item.folders="{item: presentation}">
        <v-menu tile
                v-if="presentation.folders.length">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon
                   v-bind="attrs"
                   v-on="on">
              <v-icon>
                fal fa-ellipsis-v
              </v-icon>
            </v-btn>
          </template>
          <v-list flat
                  tile
                  :elevation="0">
            <v-list-item v-for="folder in presentation.folders"
                         :key="folder.id">
              <v-list-item-content>
                <v-list-item-title>
                  {{ folder.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:item.status="{item: presentation}">
        <status-icon :status="presentation.status">
        </status-icon>
      </template>

      <template v-slot:item.created_at="{item: presentation}">
        {{ presentation.created_at | datetime('MMM D, YYYY') }}
      </template>
      <template v-slot:item.updated_at="{item: presentation}">
        {{ presentation.updated_at | datetime('MMM D, YYYY') }}
      </template>
      <template v-slot:item.expires_at="{item: presentation}">
        <template v-if="presentation.expires_at">
          {{ presentation.expires_at | datetime('MMM D, YYYY') }}
        </template>
        <template v-else>
          N/A
        </template>
      </template>

      <template v-slot:item.presentation_type="{item: presentation}">
        <v-menu tile
                bottom
                offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon
                   v-bind="attrs"
                   v-on="on">
              <presentation-icon :presentation="presentation"
                                 :title="presentation.presentation_type">
              </presentation-icon>
            </v-btn>
          </template>
          <v-list>
            <template v-if="isAdmin || presentation.owner_id === user.id">
              <v-divider v-if="1===2"></v-divider>
              <v-list-item :to="{name: 'presentation.info', params: {presentationID: presentation.id}}">
                <v-list-item-icon>
                  <v-icon>
                    fal fa-info-circle
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    Info
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item disabled>
                <v-list-item-icon>
                  <v-icon>
                    fal fa-tachometer-alt-fastest
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    Dashboard
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item disabled>
                <v-list-item-icon>
                  <v-icon>
                    fal fa-chart-line
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    Insights
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item disabled>
                <v-list-item-icon>
                  <v-icon>
                    fal fa-pencil
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    Edit
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item :to="{name: 'presentation.upload', params: {presentationID: presentation.id}}"
                           v-if="isAdmin">
                <v-list-item-icon>
                  <v-icon>
                    fal fa-upload
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    Upload
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>

    </v-data-table>

  </v-card>
</template>
<script>
import {Form} from 'vform';
import {mapGetters} from 'vuex';
import StatusIcon from '../../../components/StatusIcon';
import FilterPresentationsMenu from './FilterPresentationsMenu';
import PresentationIcon from './PresentationIcon';
import echo from '../../../plugins/echo';
import PresentationVaultIcon from '../../veeva_vault/components/PresentationVaultIcon';

export default {
  components: {PresentationVaultIcon, PresentationIcon, FilterPresentationsMenu, StatusIcon},
  data() {
    return {
      dialog: false,
      form: new Form({
        is_internal_use_only: '', // MLD-2864
        can_be_shared: '',
        can_be_forwarded: '',
        can_be_shared_link: '',
        can_be_annotated: '',
        can_be_downloaded: '',
        external_id: '',
        locale: '',
        group: '',
        sortBy: ['created_at'],
        sortDesc: [true],
        owner_id: '',
        page: 1,
        per_page: 100,
        status: 'Active',
        expires_at: {
          range: '',
          start: '',
          end: '',
        },
        created_at: {
          range: '',
          start: '',
          end: '',
        },
        updated_at: {
          range: '',
          start: '',
          end: '',
        },
        type: '',
        columns: [
          'presentation_type',
          'name',
          'external_id',
          'size',
          'created_at',
          'expires_at',
          'status',
        ]
      }),
      headers: [
        //{text: '', value: 'actions', filterable: false, sortable: false, width: '40px', class: 'px-0'},
        {text: 'Type', value: 'presentation_type', width: '40px', align: 'center'},
        {text: 'Name', value: 'name', width: '40%'},
        {text: 'Asset ID', value: 'external_id'},
        {text: 'Size', value: 'size'},
        {text: 'Created', value: 'created_at', width: '120px'},
        {text: 'Expires', value: 'expires_at', width: '120px'},
        {text: 'Active', value: 'status', width: '40px', align: 'center'},
      ],
      meta: null,
      presentations: [],
      selected: [],
    }
  },
  computed: {
    ...mapGetters([
      'groups',
      'isAdmin',
      'isMember',
      'team',
      'user',
    ]),
    selectedHeaders() {
      const headers = [];
      this.headers.forEach(header => {
        if (this.form.columns.includes(header.value)) {
          headers.push(header);
        }
      })
      return headers;
    }
  },
  created() {
    this.initForm();
    this.initEcho();
    this.fetchData();
  },
  methods: {
    initEcho() {
      echo.private(this.team.channel)
        .listen('Models.Presentation.Deleted', e => this.search)
        .listen('Models.Presentation.Processed', e => this.search)
        .listen('Models.Presentation.Updated', e => this.search)
        .listen('PresentationJobUpdated', e => this.search)
        .listen('PresentationJobDeleted', e => this.search)
    },
    initForm() {
      if (this.$route.query.sortBy) {
        this.form.sortBy = [this.$route.query.sortBy]
      }
      if (this.$route.query.sortDesc) {
        this.form.sortDesc = [this.$route.query.sortDesc]
      }
    },
    fetchData() {
      this.search();
    },
    async search() {
      if (this.form.busy) {
        return;
      }
      console.log('search', this.form.data());
      const {data} = await this.form.post(`/api/teams/${this.team.id}/presentations`)
      this.meta = data.meta;
      this.presentations = data.data;
    },
    resetFilter() {
      this.form.reset();
      this.$refs.filterForm.resetValidation();
      this.showFilterForm = false;
    },
    updateItemsPerPage(itemsPerPage) {
      if (itemsPerPage === -1) {
        itemsPerPage = 10;
      }
      this.form.per_page = itemsPerPage;
      this.search();
    },
    updatePage(page) {
      this.form.page = page;
      this.search();
    },
    updateSortBy(sortBy) {
      //console.log('updateSortBy', sortBy.map(m => m));
    },
    updateOptions(options) {
      if (options.sortBy && options.sortBy[0]) {
        console.log('updateOptions', options.sortBy, options.sortDesc);
        this.form.sortBy = options.sortBy;
        this.form.sortDesc = options.sortDesc;
        this.search();
      }
    }
  }
}
</script>
